import { useMemo } from 'react';
import { ScheduleTypes } from '@frontend/api-schedule';
import { EmptyStates } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { ChecklistField, SearchField, ListFieldProps, useFormField, useDebouncedValue } from '@frontend/design-system';
import { CALENDAR_TRACKING_IDS } from '../../trackingIds/calendar-tracking-ids';
import { FilterButtonBar } from '../ScheduleFilters/FilterButtonBar';

const trackingIds = CALENDAR_TRACKING_IDS.headerBarFilter;

type OperatoryCheckListProps = {
  checkListProps: ListFieldProps;
  locations: string[];
  selectAllIsDisabled: boolean;
  displayOperatoryList: ScheduleTypes.OperatoryType[];
};

export const OperatoryCheckList = ({
  checkListProps,
  displayOperatoryList,
  locations,
  selectAllIsDisabled,
}: OperatoryCheckListProps) => {
  const { t } = useTranslation('scheduleCalendarFilters');

  const operatorySearchField = useFormField({ type: 'text' });
  const debouncedOperatorySearchTerm = useDebouncedValue(operatorySearchField.value);

  const searchOperatories = useMemo(() => {
    return displayOperatoryList.filter(({ display_name }) => {
      const search = debouncedOperatorySearchTerm.toLowerCase().trim();
      if (!search) {
        return true;
      }
      const name = display_name || '';
      const nameMatch = name.toLowerCase().trim();
      return nameMatch?.includes(search);
    });
  }, [displayOperatoryList, debouncedOperatorySearchTerm]);

  const handleSelectAll = () => {
    checkListProps.onChange({
      name: 'schedule-operatory-filter',
      value: [...searchOperatories.map(({ value }) => value), ...(checkListProps.value ?? [])],
    });
  };

  const handleClear = () => {
    checkListProps.onChange({ value: [], name: 'schedule-operatory-filter' });
  };

  const operatoryList = useMemo(() => {
    return locations.map((location) => {
      return searchOperatories
        .filter((operatory) => operatory.locationId === location)
        .map(({ display_name, value, key }) => {
          const name = display_name || '';
          if (!value || !name) return;
          return (
            <ChecklistField.Option
              key={`${key}-${value}-${display_name}-${location}`}
              name={value}
              css={{ minWidth: 120, width: '100%', alignItems: 'left' }}
            >
              {name}
            </ChecklistField.Option>
          );
        });
    });
  }, [locations, searchOperatories]);

  return (
    <div css={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <SearchField
        name={'schedule-operatory-search'}
        placeholder={t('Search')}
        {...operatorySearchField}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
      />
      <FilterButtonBar
        onSelectAll={handleSelectAll}
        disableSelectAll={selectAllIsDisabled}
        onClear={handleClear}
        selectAllButtonTrackingId={trackingIds.selectAllOperatoriesButton}
        clearButtonTrackingId={trackingIds.clearAllOperatoriesButton}
      />
      {searchOperatories.length === 0 ? (
        <EmptyStates
          emptyStateConfig={{
            type: 'schedule',
            header: `${t('No operatories match the criteria')}`,
            description: '',
          }}
        />
      ) : (
        <>
          <ChecklistField
            name='schedule-operatory-filter'
            labelPlacement='right'
            label={''}
            css={{ maxHeight: '400px', width: '100%', overflowY: 'auto' }}
            {...checkListProps}
          >
            {operatoryList}
          </ChecklistField>
        </>
      )}
    </div>
  );
};
